import { i18nMoment } from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import CityService from 'wanderu-service-cities';
import { WapiService } from 'wanderu-service-wapi';

const logger = Logger.getLogger('pricingService');

const FIELD_MAPPING = {
    min_duration: 'minDuration',
    min_duration_datetime: 'minDurationDateTime',
    min_duration_trip_id: 'minDurationTripId',
    min_duration_tz_offset: 'minDurationTzOffset',
    min_price_datetime: 'minPriceDateTime',
    min_price_trip_id: 'minPriceTripId',
    min_price_tz_offset: 'minPriceTzOffset',
};

async function _callAggregateEndpoint(cityToCityId, fromDate, toDate, scope) {
    const body = {
        depart_datetime_min: i18nMoment.utc(fromDate).format('YYYY-MM-DD'),
        depart_datetime_max: i18nMoment.utc(toDate).format('YYYY-MM-DD'),
        scope: scope,
        c2c_id: cityToCityId,
    };

    try {
        const response = await WapiService.post('aggregate/pricing.json', { body });
        return response.result[cityToCityId.toUpperCase()][scope];
    } catch (ex) {
        logger.warn('callAggregateEndpoing', ex?.responseBody?.message, {
            params: {
                cityToCityId,
                fromDate,
                toDate,
                scope,
            },
        });
        return [];
    }
}

async function getAggregatePricesForDateRange(cityToCityId, fromDate, toDate) {
    return _mapAggregateResponse(
        await _callAggregateEndpoint(cityToCityId, fromDate, toDate, 'aggregate'),
    );
}

async function _getDailyPricesForDateRange(cityToCityId, fromDate, toDate) {
    return _mapDailyResponse(
        await _callAggregateEndpoint(cityToCityId, fromDate, toDate, 'daily'),
    );
}

function _mapAggregateResponse(response) {
    if (response.bus) {
        response.bus = _mapTransModeData(response.bus);
    }
    if (response.train) {
        response.train = _mapTransModeData(response.train);
    }
    if (response.flight) {
        response.flight = _mapTransModeData(response.flight);
    }

    return response;
}

function _mapDailyResponse(response) {
    return Object.entries(response)
        .reduce((obj, [dateKey, dateData]) => {
            obj[dateKey] = {};

            if (dateData.bus) {
                obj[dateKey].bus = _mapTransModeData(dateData.bus);
            }
            if (dateData.train) {
                obj[dateKey].train = _mapTransModeData(dateData.train);
            }
            if (dateData.flight) {
                obj[dateKey].flight = _mapTransModeData(dateData.flight);
            }

            return obj;
        }, {});
}

function _mapTransModeData(data) {
    let result = Object.entries(data)
        .reduce((obj, [key, value]) => {
            const newKey = FIELD_MAPPING[key] || key;
            delete obj[key];
            obj[newKey] = value;
            return obj;
        }, {});

    result.minPrice = data.USD.min;
    return result;
}

async function _getMinimumPricesForDateRangeGroupedByTransMode(
    currency,
    cityToCityId,
    fromDate,
    toDate,
) {
    const capitalizedCurrency = currency.toUpperCase();
    const dailyTrips = await _getDailyPricesForDateRange(cityToCityId, fromDate, toDate);

    return Object.keys(dailyTrips).reduce((accumulator, date) => {
        const tripsOnDate = dailyTrips[date];

        const minBusPrice = tripsOnDate.bus
            && tripsOnDate.bus[capitalizedCurrency]
            && tripsOnDate.bus[capitalizedCurrency].min;

        const minTrainPrice = tripsOnDate.train
            && tripsOnDate.train[capitalizedCurrency]
            && tripsOnDate.train[capitalizedCurrency].min;

        const minFlightPrice = tripsOnDate.flight
            && tripsOnDate.flight[capitalizedCurrency]
            && tripsOnDate.flight[capitalizedCurrency].min;

        accumulator[date] = {
            bus: minBusPrice,
            train: minTrainPrice,
            flight: minFlightPrice,
        };

        return accumulator;
    }, {});
}

async function _getMinimumPricesForDateRange(currency, cityToCityId, fromDate, toDate) {
    const minimumPrices = await _getMinimumPricesForDateRangeGroupedByTransMode(
        currency,
        cityToCityId,
        fromDate,
        toDate,
    );

    return Object.entries(minimumPrices).reduce((acc, [date, pricing]) => {
        const minBusPrice = pricing.bus;
        const minTrainPrice = pricing.train;
        const minFlightPrice = pricing.flight;

        const minPrices = [minBusPrice, minTrainPrice, minFlightPrice].filter(Boolean);
        acc[date] = Math.min(...minPrices);

        return acc;
    }, {});
}

async function getMinimumPricesForDateRange({
    originPlaceId, destinationPlaceId, fromDate, toDate, currency,
}) {
    const results = await Promise.all([
        CityService.getCityByPlaceId(originPlaceId),
        CityService.getCityByPlaceId(destinationPlaceId),
    ]);

    const originCity = results[0].wcityid;
    const destinationCity = results[1].wcityid;

    let departCityToCityId = `${originCity}_${destinationCity}`;
    let returnCityToCityId = `${destinationCity}_${originCity}`;

    const minimumPriceRanges = await Promise.all([
        _getMinimumPricesForDateRange(currency, departCityToCityId, fromDate, toDate),
        _getMinimumPricesForDateRange(currency, returnCityToCityId, fromDate, toDate),
    ]);

    return {
        departMinPricing: minimumPriceRanges[0],
        returnMinPricing: minimumPriceRanges[1],
    };
}

export { _callAggregateEndpoint };
export { getAggregatePricesForDateRange };
export { getMinimumPricesForDateRange }; // for testing only

export default {
    getAggregatePricesForDateRange,
    getMinimumPricesForDateRange,
};
